<template>
  <v-form autocomplete="off" class="fill-height">
    <v-card class="fill-height d-flex flex-column">
      <v-toolbar dark color="primary" flat style="max-height: 64px;">
        <v-toolbar-title>
          Receive EDN '{{ model.ednNumber }}' for Unit Number
          <router-link class="accent--text text--lighten-1" :to="{ name: 'unit', params: { unitId: model.unitId } }">{{ model.unitNumber }}</router-link>
        </v-toolbar-title>
        <slot name="header" v-if="!value.isStale"></slot>
        <v-spacer></v-spacer>
        <v-btn @click="exportPdf" color="accent">PDF Export</v-btn>
      </v-toolbar>

      <v-card-text>
        <!-- Stale Data Warning -->
        <stale-data-alert :is-stale="model.isStale || false" message="This defect notice has been updated by another user. Please refresh to see updated data." :loading="refreshing" @refresh="refreshPage"></stale-data-alert>

        <v-container grid-list-md v-if="sources && !model.isStale">
          <v-row v-if="hasWarranty">
            <v-col>
              <v-menu v-model="showWarranties" offset-y class="pa-1">
                <template v-slot:activator="{ on }">
                  <v-card v-on="on">
                    <v-alert dense border="left" type="warning">
                      This unit may be covered under warranty
                      <span class="overline" v-if="showWarranties">(hide&nbsp;</span>
                      <span class="overline" v-else>(show&nbsp;</span>
                      <v-icon v-if="showWarranties">arrow_drop_up</v-icon>
                      <v-icon v-else>arrow_drop_down</v-icon>
                      <span class="overline">)</span>
                    </v-alert>
                  </v-card>
                </template>
                <v-card class="pa-3">
                  <unit-warranty-fields :unit="warranty"></unit-warranty-fields>
                  <unit-warranty-list disabled :items="customWarranties" />
                </v-card>
              </v-menu>
            </v-col>
          </v-row>
          <v-row xs12>
            <v-col>
              <v-text-field v-model="model.ednNumber" label="EDN Number" placeholder v-validate="'required'" :error-messages="errors.first('edn')" name="edn" maxlength="30" required readonly></v-text-field>
            </v-col>
            <v-col>
              <v-text-field v-validate="roadsideIntercept" v-model="model.incidentNumber" label="Incident/Reference #" placeholder :error-messages="errors.first('reference')" name="reference" maxlength="30"></v-text-field>
            </v-col>
            <v-col>
              <v-autocomplete
                label="Unit"
                item-value="id"
                item-text="number"
                :items="units"
                no-filter
                :loading="isSearchingUnit"
                :search-input.sync="search"
                v-model="selectedUnit"
                v-validate="'required'"
                no-data-text="Start typing a Unit Name or Number"
                :error-messages="errors.collect('unit')"
                name="unit"
                required
                autocomplete="off"
                readonly
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.number }} ({{ item.garage }})</v-list-item-title>
                    <v-list-item-subtitle v-text="item.name"></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row xs12>
            <v-col cols="4">
              <v-menu v-model="receivedDateDialog" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formattedReceivedDate"
                    label="Date Received"
                    readonly
                    v-validate="'required'"
                    :error-messages="errors.first('received date')"
                    name="received date"
                    v-on="on"
                    required
                    append-icon="event"
                    @click:append="receivedDateDialog = true"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :min="minDate"
                  :max="maxDate"
                  no-title
                  v-model="model.receivedDate"
                  :readonly="this.hasBeenReceived"
                  @change="
                    receivedDateDialog = false;
                    setFormattedDates();
                  "
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="4">
              <v-select v-model="selectedSource" return-object item-text="name" :items="sources"></v-select>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="model.createdBy" label="Submitted By" readonly> </v-text-field>
            </v-col>
            <v-col> </v-col>
          </v-row>

          <div v-for="defect in model.defects">
            <v-divider class="mb-4"></v-divider>
            <v-row xs12>
              <v-col>
                <v-row>
                  <v-col cols="1">
                    <v-text-field prefix="#" v-model="defect.lineNumber" label="Defect" readonly :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"> </v-text-field>
                  </v-col>
                  <v-col cols="8">
                    <v-textarea v-model="defect.description" label="Description" auto-grow rows="1" readonly :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"> </v-textarea>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field :value="getStatus(defect.status)" label="Status" readonly :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"> </v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="defect.status === 'Scheduled' || defect.status === 'ScheduledPendingReview'">
                  <v-col cols="4">
                    <v-text-field label="Scheduled Reason Category" :value="defect.scheduledReasonCategory" disabled></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field label="Scheduled Reason Notes" :value="defect.scheduledReasonNotes" disabled></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field label="Scheduled Date" :value="defect.scheduledDate | formatDate" disabled></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="4">
                    <v-select label="Category" v-model="defect.categoryId" :items="categories" item-value="id" item-text="name" :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-select
                      label="Subcategory"
                      v-model="defect.subCategoryId"
                      :items="getUnitDefectSubCategories(defect.categoryId)"
                      item-value="id"
                      item-text="name"
                      :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"
                    ></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-autocomplete :items="availableAssignees" v-model="defect.assignees" label="Assigned" item-text="displayName" item-value="id" multiple :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'">
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on">
                          <v-list-item-action>
                            <v-checkbox :input-value="isAssigneeSelected(defect, item)" color="black"></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.displayName }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.role }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="defect.photoBlobUri ? 8 : 12">
                    <v-textarea v-model="defect.receivedNotes" label="Received Notes" row="3" auto-grow :disabled="defect.status !== 'Submitted' && defect.status !== 'Received'"> </v-textarea>
                  </v-col>
                  <v-col v-if="defect.photoBlobUri" cols="4" class="d-flex align-center">
                    <v-img :src="defect.photoBlobUri" max-width="300" max-height="240" contain class="rounded elevation-2 md-auto" style="cursor: pointer" @click="openPhotoModal(defect.photoBlobUri, defect.id)" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-spacer></v-spacer>
                  <v-btn v-show="!otherLogVisible" color="tertiary" class="mb-2 mr-5" @click="toggleChildLog(defect)">
                    Logs
                    <v-icon small v-if="showChildLog">chevron_left</v-icon>
                    <v-icon small v-else>chevron_right</v-icon>
                  </v-btn>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </v-card-text>

      <v-card-actions v-if="!model.isStale">
        <v-spacer></v-spacer>
        <slot name="actions"></slot>
        <v-btn color="red white--text" @click="deleteEdn" v-if="userIsFleetManager">Delete EDN</v-btn>
        <v-btn color="primary" :disabled="disableSubmit" @click="submit">{{ !this.hasBeenReceived ? 'Receive EDN' : 'Update' }}</v-btn>
      </v-card-actions>
    </v-card>
    <photo-modal ref="photoModal" :photo-url="photoModalData.photoUrl" :defect-id="photoModalData.defectId" @photo-deleted="handlePhotoDeleted" />
  </v-form>
</template>

<script>
import UnitWarrantyFields from '@/components/Units/Details/UnitWarrantyFields.vue';
import UnitWarrantyList from '@/components/Units/Details/UnitWarrantyList.vue';
import FormValidation from '@/components/FormValidation.vue';
import moment from 'moment/moment';
import { DateFormat } from '@/plugins/date-filter';
import { DefectSourcesStore } from '@/stores/DefectSourcesStore';
import * as _ from 'lodash';
import { Api } from '@/plugins/api';
import DefectNoticesClient from '@/components/DefectNotices/DefectNoticesClient.vue';
import StaleDataAlert from '@/components/DefectNotices/StaleDataAlert.vue';
import PhotoModal from '@/components/PhotoModal.vue';
import { Event } from '@/plugins/bus';
import DownloadHelper from '@/lib/DownloadHelper';

export default {
  props: {
    value: {
      required: true
    }
  },
  components: { UnitWarrantyFields, UnitWarrantyList, StaleDataAlert, PhotoModal },
  mixins: [FormValidation, DefectNoticesClient],
  computed: {
    minDate() {
      return moment('1900-01-01').format(DateFormat.Canonical);
    },
    maxDate() {
      return moment().format(DateFormat.Canonical);
    },
    roadsideIntercept() {
      return this.selectedSource && this.selectedSource.name === 'Roadside Intercept' ? 'required' : '';
    },
    hasBeenReceived() {
      return this.model && this.model.defects && this.model.defects.length > 0 && this.model.defects.every(x => x.status !== 'Submitted');
    }
  },
  methods: {
    async init() {
      this.submitted = false;
      this.actionNow = false;
      this.hasWarranty = false;
      this.showWarranties = false;

      this.model = { ...this.value };
      if (!this.hasBeenReceived) {
        this.model.receivedDate = moment();
      }

      this.availableAssignees = await this.getAvailableAssignees();
      this.categories = await this.getUnitDefectCategories(this.model.unitId);
      this.sources = await DefectSourcesStore.methods.getData();
      this.selectedSource = this.sources && this.model.source
        ? this.sources.find(s => s.id === this.model.source.id) || null
        : null;
      if (this.selectedSource) {
        this.model.sourceId = this.selectedSource.id;
      }

      const response = await this.$api.get(`units/${this.model.unitId}`);
      const unit = response.data;
      this.units = [unit];

      this.selectedUnit = unit;

      this.setFormattedDates();
    },
    toggleChildLog(defect) {
      this.showChildLog = !this.showChildLog;
      // let the parent know to show my log
      let event = { visible: this.showChildLog, defectId: defect.id, workSessions: defect.workSessions, assignees: defect.assignees, historicalAcceptedJobs: defect.historicalAcceptedJobs, defectNumber: defect.lineNumber };
      this.$bus.$emit(Event.ChildLogVisibilityChanged, event, defect);
    },
    isAssigneeSelected(defect, item) {
      const assignees = Array.isArray(defect.assignees) ? defect.assignees.map(a => (typeof a === 'string' ? a : a.id)) : [];
      return assignees.includes(item.id);
    },
    getStatus(status) {
      let found = this.defectStatuses.find(s => s.id === status);
      if (found) {
        return found.text;
      }
      return 'Unknown';
    },
    submitForm() {
      this.submitted = true;

      const unitNumber = this.units.filter(x => x.id === this.model.unitId)[0].number;
      const defects = this.model.defects.filter(d => d.status === 'Submitted' || d.status === 'Received').map(d => ({ ...d, assignees: Array.isArray(d.assignees) ? d.assignees.map(a => (typeof a === 'string' ? a : a.id)) : [] }));

      let model = { ...this.model, unitNumber, defects };

      this.$emit('receive', { id: this.model.id, model, actionNow: this.actionNow });
      setTimeout(() => (this.submitted = false), 1000);
    },
    deleteEdn() {
      this.$confirmCancel.show(`Delete Defect Notice?`, `<p>This will permanently delete this Defect Notice record</p>`, 'Delete it');
      var confirmHandler = confirmed => {
        this.$confirmCancel.$off('confirm-cancel', confirmHandler);
        if (!confirmed) {
          return;
        }

        this.$emit('delete-edn', { id: this.value.id, source: 'onroad-receive' });
      };
      this.$confirmCancel.$on('confirm-cancel', confirmHandler);
    },
    setFormattedDates() {
      this.formattedReceivedDate = this.model.receivedDate ? moment(this.model.receivedDate).format(DateFormat.DefaultDisplay) : '';
    },
    showWarrantiesMenu(e) {
      e.preventDefault();
      this.showWarranties = false;
      this.$nextTick(() => {
        this.showWarranties = true;
      });
    },
    performSearch: _.debounce(async (val, me) => {
      if (!me.search) {
        me.search = me.selectedUnit ? me.selectedUnit.name : '';
      }
      if (!me.search) {
        return;
      }

      try {
        me.isSearchingUnit = true;
        const response = await me.$api.get(`units?searchTerm=${me.search}&unitType='both'&orderByUnitMatch=true`);
        me.units = response.data.data;
      } catch {
        me.$toast.error('There was an error getting units');
      }
      me.isSearchingUnit = false;
    }, 200),
    async getUnitDefectCategories(unitId) {
      try {
        const unitFilter = unitId === undefined ? '' : `?unitId=${unitId}`;
        const response = await Api.get(`defect-categories${unitFilter}`);
        return response.data;
      } catch (error) {
        this.$toast.error('There was an error getting defect categories');
        return [];
      }
    },
    getUnitDefectSubCategories(categoryId) {
      var category = this.categories.find(x => x.id === categoryId);
      if (!category) {
        return [];
      }
      return category.subCategories;
    },
    async refreshPage() {
      this.refreshing = true;
      try {
        // Emit an event to the parent to refresh the defect page
        this.$emit('refresh-page');

        // Close the modal
        this.$emit('close-modal');
      } catch (error) {
        this.$toast.error('Failed to refresh page');
      } finally {
        this.refreshing = false;
      }
    },
    openPhotoModal(photoUrl, defectId) {
      this.photoModalData.photoUrl = photoUrl;
      this.photoModalData.defectId = defectId;
      this.$refs.photoModal.showModal();
    },
    handlePhotoDeleted() {
      let defect = this.model.defects.find(d => d.id === this.photoModalData.defectId);
      defect.photoBlobUri = '';
      this.photoModalData.photoUrl = '';
    },
    async onParentLogVisibilityChanged(event) {
      // toggle my own defect visibility based on my parent's visibility
      this.otherLogVisible = event.visible;
    },
    async onAnotherChildLogVisibilityChanged(event) {
      // toggle my own visibility based on my sibling's visibility
      // TODO: this.defect doesn't work here. We've got multiple defects on this screen.
      if (event.defectId !== this.defect.id) {
        this.otherLogVisible = event.visible;
        this.$refs.photoModal.close();
      }
    },
    async exportPdf() {
      try {
        this.$wait.start('exporting defects pdf');
        const response = await this.$api.get(`defect-notices/${this.model.id}/generate/pdf`, {
          responseType: 'blob'
        });
        let filename = `edn_${this.model.ednNumber}.pdf`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        const blob = new Blob([response.data], { type: 'application/pdf' });
        DownloadHelper.download(filename, blob);
      } catch (err) {
        this.$toast.error('There was an error generating the PDF report');
      } finally {
        this.$wait.end('exporting defects pdf');
      }
    }
  },
  data() {
    return {
      model: {},
      submitted: false,
      units: [],
      isSearchingUnit: false,
      search: null,
      selectedUnit: null,
      selectedSource: null,
      sources: [],
      categories: [],
      subCategories: [],
      receivedDateDialog: false,
      editing: false,
      formattedReceivedDate: null,
      actionNow: false,
      hasWarranty: false,
      showWarranties: false,
      refreshing: false,
      warranty: {
        bumperToBumperWarrantyStart: null,
        bumperToBumperWarrantyEnd: null,
        engineWarrantyStart: null,
        engineWarrantyEnd: null,
        transmissionWarrantyStart: null,
        transmissionWarrantyEnd: null,
        bodyWarrantyStart: null,
        bodyWarrantyEnd: null
      },
      customWarranties: [],
      photoModalData: {
        photoUrl: '',
        defectId: null
      },
      showLog: false,
      showChildLog: false,
      otherLogVisible: false
    };
  },
  watch: {
    async selectedUnit() {
      this.hasWarranty = false;
      this.model.unitId = this.selectedUnit.id;
      if (!this.model.unitId) {
        return;
      }

      try {
        const response = await this.$api.get(`units/${this.model.unitId}`);
        this.hasWarranty = response.data.isUnderWarranty || response.data.isUnderCustomWarranty;
        if (this.hasWarranty) {
          this.warranty.bumperToBumperWarrantyStart = response.data.bumperToBumperWarrantyStart;
          this.warranty.bumperToBumperWarrantyEnd = response.data.bumperToBumperWarrantyEnd;
          this.warranty.engineWarrantyStart = response.data.engineWarrantyStart;
          this.warranty.engineWarrantyEnd = response.data.engineWarrantyEnd;
          this.warranty.transmissionWarrantyStart = response.data.transmissionWarrantyStart;
          this.warranty.transmissionWarrantyEnd = response.data.transmissionWarrantyEnd;
          this.warranty.bodyWarrantyStart = response.data.bodyWarrantlyStart;
          this.warranty.bodyWarrantyEnd = response.data.bodyWarrantlyEnd;
          this.warranty.subjectToCampaign = response.data.subjectToCampaign;
          this.warranty.subjectToCampaignNotes = response.data.subjectToCampaignNotes;
          this.customWarranties = response.data.customWarrantyDates;
        } else {
          this.warranty.bumperToBumperWarrantyStart = null;
          this.warranty.bumperToBumperWarrantyEnd = null;
          this.warranty.engineWarrantyStart = null;
          this.warranty.engineWarrantyEnd = null;
          this.warranty.transmissionWarrantyStart = null;
          this.warranty.transmissionWarrantyEnd = null;
          this.warranty.bodyWarrantyStart = null;
          this.warranty.bodyWarrantyEnd = null;
          this.customWarranties = [];
        }
      } catch (error) {
        this.$toast.error('There was an error checking warranty status for the selected unit');
      }
    },
    selectedSource() {
      if (this.selectedSource) {
        this.model.sourceId = this.selectedSource.id;
        this.model.source = this.selectedSource;
      } else {
        this.model.sourceId = null;
        this.model.source = null;
      }
    },
    async search(val) {
      this.performSearch(val, this);
    }
  },
  async created() {
    this.$bus.$on(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$on(Event.ChildLogVisibilityChanged, this.onAnotherChildLogVisibilityChanged);
  },
  destroyed() {
    this.$bus.$off(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$off(Event.ChildLogVisibilityChanged, this.onAnotherChildLogVisibilityChanged);
    this.showChildLog = false;
  }
};
</script>

<style scoped>
.custom-checkbox .v-input--selection-controls__ripple {
  background-color: transparent !important;
}

.custom-checkbox .v-icon:not(.v-icon--link):not(.v-chip__close) {
  color: inherit !important; /* Prevents changing the icon color to green */
}

.custom-checkbox .v-input--selection-controls__input {
  color: inherit !important; /* Prevents checkbox from turning green */
}
</style>
